import {
    useCallback, useEffect, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useEnsureValidToken } from './useEnsureValidToken';
import { getReturnOps } from '../redux/http/request';
import { AppDispatch, RootState } from '../configureStore';
import { ConnectionStatus, InFlightMessage, InFlightResponse } from '../interface/returnOps';
import {
    REMOVE_INFLIGHT_MESSAGE,
    SET_INFLIGHT_CONNECTION_STATUS,
    SET_INFLIGHT_MESSAGES,
    UPSERT_INFLIGHT_MESSAGE,
} from '../redux/reducer/returnOps';

const RETURN_BACKEND = import.meta.env.VITE_RETURN_BACKEND || 'http://localhost:4001';
const CLEANUP_DELAY_MS = 30_000;
const POLL_INTERVAL_MS = 5_000;

export const useInFlightMessages = () => {
    const dispatch = useDispatch<AppDispatch>();
    const ensureValidToken = useEnsureValidToken();
    const messages = useSelector<RootState, InFlightMessage[]>(
        (state) => state.returnOps.inFlightMessages,
    );
    const connectionStatus = useSelector<RootState, ConnectionStatus>(
        (state) => state.returnOps.inFlightConnectionStatus,
    );
    const abortRef = useRef<AbortController | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const cleanupTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const mountedRef = useRef(true);

    const scheduleCleanup = useCallback((messageId: string) => {
        const existing = cleanupTimersRef.current.get(messageId);
        if (existing) clearTimeout(existing);

        const timer = setTimeout(() => {
            if (!mountedRef.current) return;
            dispatch({ type: REMOVE_INFLIGHT_MESSAGE, messageId });
            cleanupTimersRef.current.delete(messageId);
        }, CLEANUP_DELAY_MS);

        cleanupTimersRef.current.set(messageId, timer);
    }, [dispatch]);

    const upsertMessage = useCallback((msg: InFlightMessage) => {
        dispatch({ type: UPSERT_INFLIGHT_MESSAGE, data: msg });

        if (msg.status === 'completed' || msg.status === 'failed') {
            scheduleCleanup(msg.messageId);
        }
    }, [dispatch, scheduleCleanup]);

    const handleEvent = useCallback((eventType: string, data: string) => {
        try {
            const parsed = JSON.parse(data);
            const payload = parsed.data ?? parsed;
            if (eventType === 'snapshot') {
                const msgs = payload as InFlightMessage[];
                dispatch({ type: SET_INFLIGHT_MESSAGES, data: msgs });
                msgs.forEach((m) => {
                    if (m.status === 'completed' || m.status === 'failed') {
                        scheduleCleanup(m.messageId);
                    }
                });
            } else {
                upsertMessage(payload as InFlightMessage);
            }
        } catch {
            // Ignore malformed events
        }
    }, [dispatch, scheduleCleanup, upsertMessage]);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const pollFallback = useCallback(async () => {
        stopPolling();
        dispatch({ type: SET_INFLIGHT_CONNECTION_STATUS, status: 'polling' });

        const poll = async () => {
            try {
                const token = await ensureValidToken();
                const res = await getReturnOps('/api/inflight', {}, undefined, undefined, token);
                if (mountedRef.current) {
                    const response = res.data as InFlightResponse;
                    dispatch({ type: SET_INFLIGHT_MESSAGES, data: response.messages });
                }
            } catch {
                // Silently fail, will retry on next interval
            }
        };

        await poll();
        pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    }, [dispatch, ensureValidToken, stopPolling]);

    const connect = useCallback(async () => {
        if (abortRef.current) abortRef.current.abort();
        stopPolling();

        const ctrl = new AbortController();
        abortRef.current = ctrl;
        dispatch({ type: SET_INFLIGHT_CONNECTION_STATUS, status: 'connecting' });

        try {
            const token = await ensureValidToken();

            await fetchEventSource(`${RETURN_BACKEND}/api/inflight/stream`, {
                signal: ctrl.signal,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                onopen: async (response) => {
                    if (response.ok) {
                        if (mountedRef.current) dispatch({ type: SET_INFLIGHT_CONNECTION_STATUS, status: 'connected' });
                    } else {
                        throw new Error(`SSE open failed: ${response.status}`);
                    }
                },
                onmessage: (event) => {
                    if (!mountedRef.current) return;
                    handleEvent(event.event, event.data);
                },
                onerror: () => {
                    if (mountedRef.current) pollFallback();
                    throw new Error('SSE error, switching to polling');
                },
            });
        } catch {
            if (mountedRef.current && connectionStatus !== 'polling') {
                pollFallback();
            }
        }
    }, [dispatch, ensureValidToken, handleEvent, pollFallback, stopPolling, connectionStatus]);

    useEffect(() => {
        mountedRef.current = true;
        connect();

        return () => {
            mountedRef.current = false;
            if (abortRef.current) abortRef.current.abort();
            stopPolling();
            cleanupTimersRef.current.forEach((timer) => clearTimeout(timer));
            cleanupTimersRef.current.clear();
        };
     
    }, []);

    return { messages, connectionStatus, reconnect: connect };
};

export default useInFlightMessages;
